import React, { useState, useEffect, FC } from "react";
import Content from "../../layout/content/Content";
import { Card } from "reactstrap";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
} from "../../components/Component";
import { Steps, Step } from "react-step-builder";
import { useForm, FormProvider } from "react-hook-form";
import { useLocation, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { message, Spin, Flex } from "antd";
import dayjs from "dayjs";

// Import step components
import WorkRequestGeneralDetails from "./workRequestCreation/workRequestGeneralDetails";
import WorkRequestDetails from "./workRequestCreation/workRequestDetails";
import WorkRequestPreview from "./workRequestCreation/workRequestPreview";
import Header_WorkRequest from "./workRequestCreation/Header_WorkRequest";

// Import types and data
import {
  RequestForType,
  WorkRequestFormData,
  WorkRequestStatus,
} from "./types";
import {
  generateWorkRequestNumber,
  getMasterData,
} from "./mockData/workRequestData";
import workRequestApis from "../../api/WorkOrderManagement/WorkRequestApis";

const config = {
  before:
    Header_WorkRequest as React.ComponentType<{}> as () => React.ReactElement,
};

const WorkRequestCreationMain: FC = () => {
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Check if editing or duplicating existing work request
  const editId = searchParams.get("edit");
  const duplicateId = searchParams.get("duplicate");
  const isEditMode = !!editId;
  const isDuplicateMode = !!duplicateId;

  const formMethods = useForm<WorkRequestFormData>({
    mode: "onChange",
    defaultValues: {
      // General Information - Following FSD specs exactly
      workRequestNumber: "",
      workRequestTitle: "",
      description: "",
      siteId: "",
      siteName: "",
      projectId: "",
      projectName: "",
      requestFor: RequestForType.ConstructionWork,
      plannedStartDate: null,
      plannedEndDate: null,
      approvalRequired: "false", // String for form handling

      // Work Request Lines
      workRequestLines: [],

      // Internal fields
      status: WorkRequestStatus.Draft,
      type: isEditMode ? "edit" : isDuplicateMode ? "duplicate" : "create",
      createdBy: "Current User",
    },
  });

  const { watch, setValue, reset } = formMethods;

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);

    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  useEffect(() => {
    // Auto-generate Work Request Number if creating new (FSD requirement)
    if (!editId && !duplicateId) {
      generateAndSetWorkRequestNumber();
    }

    // Load existing data if editing or duplicating
    if (editId) {
      loadWorkRequestData(editId);
    } else if (duplicateId) {
      loadWorkRequestDataForDuplicate(duplicateId);
    }
  }, [editId, duplicateId]);

  const viewChange = () => {
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  // Auto-generate Work Request Number with FSD format
  const generateAndSetWorkRequestNumber = () => {
    const wrNumber = generateWorkRequestNumber();
    setValue("workRequestNumber", wrNumber);
  };

  const loadWorkRequestData = async (id: string) => {
    try {
      setLoading(true);

      if (!id) {
        message.error("Work Request ID is required");
        return;
      }

      console.log("Loading Work Request for edit with ID:", id);

      // Call the actual API
      const response: any = await workRequestApis.getWorkRequestById(id);

      if (
        response &&
        response.status &&
        response.data &&
        response.data.workRequest
      ) {
        const apiData = response.data.workRequest;
        console.log("Loaded Work Request data for edit:", apiData);

        // Map work request lines with proper structure for form
        const mappedWorkRequestLines =
          apiData.workRequestLines?.map((line: any, index: number) => ({
            id: line.workRequestLineId?.toString() || `line_${index}`,
            workRequestLineId: line.workRequestLineId, // Preserve for updates
            taskTitle: line.taskTitle || "",
            taskDescription: line.taskDescription || "",
            workCategory: line.workCategoryId?.toString() || "",
            workCategoryName: line.workCategoryName || "",
            workSubCategory: line.workSubCategoryId?.toString() || "",
            workSubCategoryName: line.workSubCategoryName || "",
            workCategoryId: line.workCategoryId, // Preserve category IDs
            workSubCategoryId: line.workSubCategoryId,
            quantity: line.quantity || 0,
            uom: line.uomId?.toString() || "",
            uomName: line.uomName || "",
            uomId: line.uomId, // Preserve UOM ID
            source:
              line.sourceType === "Project Tasks"
                ? "Project Management"
                : line.sourceType === "Work BOQ"
                ? "Work BOQ"
                : line.sourceType || "Project Management",
            sourceId: line.sourceId?.toString() || "",
            // Additional task information
            task: line.task
              ? {
                  taskId: line.task.taskId,
                  taskSequenceId: line.task.taskSequenceId,
                  taskName: line.task.taskName,
                  taskDescription: line.task.taskDescription,
                  taskStatus: line.task.taskStatus,
                  taskBoqs: line.task.taskBoqs || [],
                }
              : null,
          })) || [];

        // Determine the selectedSource from the first work request line
        let selectedSource: any;
        if (mappedWorkRequestLines.length > 0) {
          const firstLineSourceType = apiData.workRequestLines[0]?.sourceType;
          if (firstLineSourceType === "Project Tasks") {
            selectedSource = "Project Management";
          } else if (firstLineSourceType === "Work BOQ") {
            selectedSource = "Work BOQ";
          } else {
            selectedSource = "Project Management"; // default
          }
        }

        // Set form values from API data
        setValue("workRequestId", apiData.workRequestId || id); // Set the ID for updates
        setValue("workRequestNumber", apiData.workRequestSequenceId || "");
        setValue("workRequestTitle", apiData.workRequestTitle || "");
        setValue("description", apiData.description || "");
        setValue("siteId", apiData.siteId?.toString() || "");
        setValue("siteName", apiData.siteName || "");
        setValue("projectId", apiData.projectId?.toString() || "");
        setValue("projectName", apiData.projectTitle || "");
        setValue(
          "requestFor",
          (apiData.requestFor as RequestForType) ||
            RequestForType.ConstructionWork
        );
        setValue("plannedStartDate", apiData.plannedStartDate || null);
        setValue("plannedEndDate", apiData.plannedEndDate || null);
        setValue(
          "approvalRequired",
          apiData.approvalRequired ? "true" : "false"
        );
        setValue("workRequestLines", mappedWorkRequestLines);
        setValue(
          "status",
          (apiData.status as WorkRequestStatus) || WorkRequestStatus.Draft
        );
        setValue("type", "edit");

        // Set selectedSource and lock state for edit mode
        if (selectedSource) {
          setValue("selectedSource", selectedSource);
          setValue("isSourceLocked", true); // Lock source when editing existing work request
        }

        // Set approval hierarchy data for edit mode
        if (
          apiData.workRequestApprovals &&
          apiData.workRequestApprovals.length > 0
        ) {
          const approvalData = apiData.workRequestApprovals.map(
            (approval: any) => ({
              roleId: approval.roleId,
              level: approval.level,
              userId: approval.userId,
              approvalStage: approval.approvalStage,
              roleName: approval.roleName,
              userName: approval.userName || approval.approverName,
            })
          );
          setValue(
            "approvalsData",
            JSON.stringify({ open: false, data: approvalData })
          );
        }

        message.success("Work request data loaded successfully");
      } else {
        throw new Error(
          response?.message || "Failed to fetch Work Request details"
        );
      }
    } catch (error: any) {
      console.error("Error loading work request:", error);
      message.error("Failed to load work request data");
    } finally {
      setLoading(false);
    }
  };

  const loadWorkRequestDataForDuplicate = async (id: string) => {
    try {
      setLoading(true);

      if (!id) {
        message.error("Work Request ID is required");
        return;
      }

      console.log("Loading Work Request for duplicate with ID:", id);

      // Call the actual API
      const response: any = await workRequestApis.getWorkRequestById(id);

      if (
        response &&
        response.status &&
        response.data &&
        response.data.workRequest
      ) {
        const apiData = response.data.workRequest;
        console.log("Loaded Work Request data for duplicate:", apiData);

        // Generate new number for duplicate
        generateAndSetWorkRequestNumber();

        // Map work request lines with new IDs for duplicate
        const mappedWorkRequestLines =
          apiData.workRequestLines?.map((line: any, index: number) => ({
            id: `duplicate_${Date.now()}_${index}`, // Generate new IDs for duplicate
            taskTitle: line.taskTitle || "",
            taskDescription: line.taskDescription || "",
            workCategory: line.workCategoryId?.toString() || "",
            workCategoryName: line.workCategoryName || "",
            workSubCategory: line.workSubCategoryId?.toString() || "",
            workSubCategoryName: line.workSubCategoryName || "",
            quantity: line.quantity || 0,
            uom: line.uomId?.toString() || "",
            uomName: line.uomName || "",
            source:
              line.sourceType === "Project Tasks"
                ? "Project Management"
                : line.sourceType === "Work BOQ"
                ? "Work BOQ"
                : line.sourceType || "Project Management",
            sourceId: line.sourceId?.toString() || "",
            // Additional task information
            task: line.task
              ? {
                  taskId: line.task.taskId,
                  taskSequenceId: line.task.taskSequenceId,
                  taskName: line.task.taskName,
                  taskDescription: line.task.taskDescription,
                  taskStatus: line.task.taskStatus,
                  taskBoqs: line.task.taskBoqs || [],
                }
              : null,
          })) || [];

        // Determine the selectedSource from the first work request line
        let selectedSource: any;
        if (mappedWorkRequestLines.length > 0) {
          const firstLineSourceType = apiData.workRequestLines[0]?.sourceType;
          if (firstLineSourceType === "Project Tasks") {
            selectedSource = "Project Management";
          } else if (firstLineSourceType === "Work BOQ") {
            selectedSource = "Work BOQ";
          } else {
            selectedSource = "Project Management"; // default
          }
        }

        // Copy data but reset status and IDs for duplicate
        setValue("workRequestTitle", `Copy of ${apiData.workRequestTitle}`);
        setValue("description", apiData.description || "");
        setValue("siteId", apiData.siteId?.toString() || "");
        setValue("siteName", apiData.siteName || "");
        setValue("projectId", apiData.projectId?.toString() || "");
        setValue("projectName", apiData.projectTitle || "");
        setValue(
          "requestFor",
          (apiData.requestFor as RequestForType) ||
            RequestForType.ConstructionWork
        );
        setValue("plannedStartDate", null); // Reset dates for duplicate
        setValue("plannedEndDate", null);
        setValue(
          "approvalRequired",
          apiData.approvalRequired ? "true" : "false"
        );
        setValue("workRequestLines", mappedWorkRequestLines);
        setValue("status", WorkRequestStatus.Draft);
        setValue("type", "duplicate");

        // Set selectedSource and lock state for duplicate mode
        if (selectedSource) {
          setValue("selectedSource", selectedSource);
          setValue("isSourceLocked", true); // Lock source when duplicating existing work request
        }

        message.success("Work request duplicated successfully");
      } else {
        throw new Error(
          response?.message || "Failed to fetch Work Request details"
        );
      }
    } catch (error: any) {
      console.error("Error duplicating work request:", error);
      message.error("Failed to duplicate work request");
    } finally {
      setLoading(false);
    }
  };

  const getPageTitle = () => {
    if (isEditMode) return `Edit Work Request - ${watch("workRequestNumber")}`;
    if (isDuplicateMode)
      return `Duplicate Work Request - ${watch("workRequestNumber")}`;
    return "New Work Request";
  };

  const getPageSubtitle = () => {
    if (isEditMode) return "Modify existing work request details";
    if (isDuplicateMode)
      return "Create new work request from existing template";
    return "Create a new work request following the three-step process";
  };

  return (
    <>
      <Head title="Work Request Creation" />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}

              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">{getPageTitle()}</BlockTitle>
                    {/* <p className="text-muted mt-1">{getPageSubtitle()}</p> */}
                    {watch("workRequestNumber") && (
                      <div className="mt-2">
                        <span className="badge badge-outline-primary">
                          WR #: {watch("workRequestNumber")}
                        </span>
                        {watch("status") && (
                          <span className="badge badge-outline-info ms-2">
                            Status: {watch("status")}
                          </span>
                        )}
                      </div>
                    )}
                  </BlockHeadContent>

                  <BlockHeadContent>
                    <Link
                      to={(() => {
                        const searchParams = new URLSearchParams(
                          location.search
                        );
                        const flowType = searchParams.get("flowType");
                        const flowTypeQuery = flowType
                          ? `?flowType=${flowType}`
                          : "";
                        return `/work-request-list${flowTypeQuery}`;
                      })()}
                    >
                      <Button
                        outline
                        className="bg-white d-none d-sm-inline-flex"
                      >
                        <Icon name="arrow-left"></Icon>
                        <span>Back to List</span>
                      </Button>
                      <Button
                        outline
                        className="btn-icon bg-white d-inline-flex d-sm-none"
                      >
                        <Icon name="arrow-left"></Icon>
                      </Button>
                    </Link>
                  </BlockHeadContent>

                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <Block className="mt-1">
                {loading ? (
                  <Flex align="center" gap="middle" className="pageLoader">
                    <Spin size="large" className="m-auto" />
                    <span>Loading work request data...</span>
                  </Flex>
                ) : (
                  <div className="nk-wizard nk-wizard-simple is-alter wizard clearfix">
                    <FormProvider {...formMethods}>
                      <Steps config={config}>
                        {/* Step 1: General Information (FSD Section 1) */}
                        <Step component={WorkRequestGeneralDetails} />

                        {/* Step 2: Work Request Details (FSD Section 2) */}
                        <Step component={WorkRequestDetails} />

                        {/* Step 3: Preview & Submit (FSD Section 3) */}
                        <Step component={WorkRequestPreview} />
                      </Steps>
                    </FormProvider>
                  </div>
                )}
              </Block>
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default WorkRequestCreationMain;
